import { useEffect, useContext, useState, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { ProviderError } from '@atlaskit/editor-common/collab';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useEditorFlagsContext } from '@confluence/editor-flags/entry-points/EditorFlagsContext.ts';
import { ALLOWED_FLAG_MESSAGES } from '@confluence/editor-flags/entry-points/allowedFlagConstants.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';

import type { CollabRecoveryErrorType } from './useError.ts';

export type UseCollabInitTimeoutProps = {
	contentId: string;
	contentType: string;
	collabEditInitialized: boolean;
	collabRecoveryError: CollabRecoveryErrorType | null;
	collabEditError: ProviderError | null;
	isEditPageDraftRestricted: boolean;
};

export const useCollabInitTimeout = ({
	contentId,
	contentType,
	collabEditInitialized,
	collabRecoveryError,
	collabEditError,
	isEditPageDraftRestricted,
}: UseCollabInitTimeoutProps): {
	collabInitTimedOut: boolean;
} => {
	const [collabInitTimedOut, setCollabInitTimedOut] = useState<boolean>(false);

	const { showEditorFlag } = useEditorFlagsContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { edition } = useSessionData();
	const COLLAB_INIT_TIMEOUT_MS: number = StatsigConfigurations.getNumber(
		'confluence_frontend_set_collab_init_timeout',
		0,
	); // default to 0 if FF not set, meaning we don't wait to show the flag
	const experienceTracker = useContext(ExperienceTrackerContext);
	const prevContentId = useRef<string>(contentId);

	useEffect(() => {
		if (contentId !== prevContentId.current) {
			// Short circuit on contentId mismatch
			prevContentId.current = contentId;
			setCollabInitTimedOut(false);
			return;
		}

		// short-circuit when:
		//      * new collab service is already initialized
		//      * COLLAB_INIT_TIMEOUT_MS has not been set
		//      * collabRecoveryError is defined (as we display a distinct flag for recovery error)
		//      * collabEditError unrecoverable or permission/not found error received over websocket, we have separate error handling code paths
		if (
			collabEditInitialized ||
			COLLAB_INIT_TIMEOUT_MS === 0 ||
			collabRecoveryError ||
			collabEditError ||
			isEditPageDraftRestricted
		) {
			setCollabInitTimedOut(false);
			return;
		}

		const timeout = setTimeout(() => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'editorLoadTimeout',
					actionSubject: 'nativeEditor',
					source: 'confluenceFrontend',
					objectId: contentId,
					objectType: contentType,
					attributes: {
						timeoutMs: COLLAB_INIT_TIMEOUT_MS,
						edition,
					},
				},
			}).fire();

			showEditorFlag({
				errorName: ALLOWED_FLAG_MESSAGES.COLLAB_INIT_TIMEOUT_TAKING_TOO_LONG,
			});

			experienceTracker.attachDebugPoint({
				name: ALLOWED_FLAG_MESSAGES.COLLAB_INIT_TIMEOUT_TAKING_TOO_LONG,
				message: `NCS Connect Timeout after ${COLLAB_INIT_TIMEOUT_MS}`,
			});

			setCollabInitTimedOut(true);
		}, COLLAB_INIT_TIMEOUT_MS);

		return () => {
			clearTimeout(timeout);
			setCollabInitTimedOut(false);
		};
	}, [
		collabEditInitialized,
		collabRecoveryError,
		collabEditError,
		contentId,
		contentType,
		edition,
		COLLAB_INIT_TIMEOUT_MS,
		showEditorFlag,
		createAnalyticsEvent,
		isEditPageDraftRestricted,
		experienceTracker,
	]);

	return { collabInitTimedOut };
};
